import React, { useState } from 'react';
import { Menu, Bell, Settings, ChevronDown, User, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { SmartNotifications } from '../notifications/SmartNotifications';
import { WalletConnector } from '../wallet/WalletConnector';
import { StealthModeToggle } from '../stealth-mode/StealthModeToggle';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { useAuth } from '../../hooks/useAuth';

interface HeaderProps {
  onMenuToggle: () => void;
  sidebarOpen: boolean;
}

export function Header({ onMenuToggle, sidebarOpen }: HeaderProps) {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const { userData, logout } = useAuth();
  
  // User data from context or fallback
  const user = {
    name: userData?.name || 'Nardium User',
    email: userData?.email || 'user@nardium.com',
    initials: userData?.name ? userData.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'NU'
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="bg-white dark:bg-[#141414] border-b border-slate-200 dark:border-[#262626] px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onMenuToggle}
            className="lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-white text-sm font-bold">N</span>
            </div>
            <h1 className="text-xl font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
              Nardium
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Stealth Mode Toggle */}
          <StealthModeToggle />

          {/* Wallet Connector */}
          <WalletConnector />

          {/* Notifications */}
          <Button
            variant="ghost"
            size="sm"
            className="relative"
            onClick={() => setNotificationsOpen(!notificationsOpen)}
          >
            <Bell className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">
              3
            </span>
          </Button>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <Avatar className="w-6 h-6">
                  <AvatarFallback className="text-xs bg-gradient-to-r from-violet-600 to-purple-600 text-white">
                    {user.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">
                    {user.name}
                  </span>
                  <span className="text-xs text-slate-500">
                    {user.email}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem>
                <User className="w-4 h-4 mr-2" />
                <span className="font-medium">Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-600 dark:text-red-400"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      <SmartNotifications 
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
    </header>
  );
}